/**
 * The imperative three.js WebGPU side of `/fly`: renderer lifecycle, the world,
 * the aircraft mesh, the two cameras, and the handle a route drives them all
 * through. Kept out of `Fly.tsx` so the route stays JSX-and-wiring, mirroring
 * how `/assets` splits `three-model-scene.ts` from `ModelViewer.tsx`.
 *
 * Construction lives here; the per-frame work and the state it reads live in
 * `scene-loop.ts`, and the asynchronous boot in `scene-boot.ts`.
 */

import * as THREE from "three/webgpu";
import { AUTHENTIC_STICK_SHAPING, bakedModelIdOf, CENTRED_STICK, stickShapingFor, type MissionRegion } from "@red-opus/engine";
import { createFlightAudioEngine } from "../audio/engine.ts";
import { DEFAULT_AUDIO_SETTINGS } from "../audio/settings.ts";
import { disposeTree } from "../three/dispose-tree.ts";
import { DEFAULT_EFFECTS_SETTINGS } from "./scene-propellers.ts";
import { chaseCameraGoal, FLY_BY_ZOOM } from "./camera-follow.ts";
import { DEFAULT_CAMERA_MODE } from "./camera-modes.ts";
import { chooseCameraMode, chooseCameraTarget, playerAircraftKey } from "./scene-camera.ts";
import { eyeOffsetFor } from "./cockpit-view.ts";
import { createCombatVisuals } from "./combat-visuals.ts";
import { createParticleEffects } from "./scene-particles.ts";
import { applyEffectsSettings, resizeViewport, watchInit } from "./scene-boot.ts";
import { observeViewMetrics } from "../three/view-metrics.ts";
import { createSearchlights } from "./scene-searchlights.ts";
import { createGroundShadows } from "./scene-shadows.ts";
import { createParachuteVisual } from "./scene-parachute.ts";
import { createCrashMarks } from "./scene-crash-marks.ts";
import { createAmbientJumpers } from "./scene-ambient-jumpers.ts";
import { createSpriteEffects } from "./sprite-effects.ts";
import { buildCamera, buildRenderer } from "./flight-view.ts";
import { currentRendererPlan } from "../three/renderer-plan.ts";
import { silentFlight } from "./scene-audio.ts";
import { GunMuzzles } from "./gun-muzzles.ts";
import { NO_COCKPIT_HITS } from "./scene-cockpit-report.ts";
import { createDashCanvasFeed } from "./cockpit-dash-quad.ts";
import { createCockpit3dQuad } from "./scene-cockpit-3d.ts";
import { createMuzzleFlashState } from "./cockpit-interior.ts";
import { createCockpitInterior } from "../three/cockpit-interior.ts";
import { createKillCamWindow, dismissKillCam, restoreKillCam, setKillCamPlacement } from "./scene-killcam.ts";
import { sunSuppressionFor } from "./sun-blind-spot.ts";
import { endSortie, reportMission } from "./mission-report.ts";
import { sceneRunnerFor } from "./scene-runner.ts";
import { createSkyDressing } from "./sky-dressing.ts";
import { skyTreatmentFor } from "./effects-settings.ts";
import { startLoop, type SceneState } from "./scene-loop.ts";
import { IDLE_TIME_COMPRESSION } from "./time-compression.ts";
import { IDLE_AUTOPILOT } from "./autopilot.ts";
import { IDLE_COCKPIT_VIEW, type FlightScene, type FlightSceneCallbacks, type FlightSceneOptions } from "./flight-scene-api.ts";

// The scene's contract — callbacks, options, view state — lives in
// `flight-scene-api.ts`, which imports no `three`; this file is the imperative
// half. Re-exported here so a route still takes the whole scene from one import.
export * from "./flight-scene-api.ts";

/** The instant dogfight's airframe: `CAMEL.TBL`, best renderable LOD (164 triangles). */
const DEFAULT_PLAYER_MODEL = bakedModelIdOf("CAMEL") ?? "models/CAMEL";

/**
 * Creates and owns one flight scene attached to `container`. Assumes the
 * caller has already feature-detected WebGPU (`hasWebGpu`); nothing here
 * outlives `dispose()`.
 */
/** Builds AD-021's canopy and puts it in the world. Hidden until somebody jumps. */
function mountParachute(scene: THREE.Scene) {
  const visual = createParachuteVisual();
  scene.add(visual.group);
  return visual;
}

export function createFlightScene(
  container: HTMLDivElement,
  callbacks: FlightSceneCallbacks,
  options: FlightSceneOptions = {},
): FlightScene {
  const { groundRef, sceneryRef, runner, missionRunner, isDogfight } = sceneRunnerFor(options);
  // Read once, at the top, so the renderer built below and the boot that may
  // rebuild it are working from the same decision rather than each re-reading a
  // store the first of them might have written to.
  const rendererPlan = options.rendererPlan ?? currentRendererPlan();
  const scene = new THREE.Scene();
  // Sky, ground and the mission's sun or moon come as one group, so the hour's
  // whole share of the picture moves together (`sky-dressing.ts`, which also
  // records why there is still no `scene.fog`).
  const effects = options.effects ?? DEFAULT_EFFECTS_SETTINGS;
  // `SkyDressingContext` documents what each field decides; a dogfight or a duel
  // briefs no mission and so gets no sky word, no seed and no cloud.
  const dressing = createSkyDressing(options.mission?.weather.timeOfDay, skyTreatmentFor(effects, options.skyOverride), {
    sky: options.mission?.weather.sky,
    seed: options.mission?.seed ?? 0,
    realisticWeather: options.mission?.realism.realisticWeather !== false,
    // When and where, for the ENHANCED night sky's ephemeris
    // (`night-sky-look.ts`). The career date beats the era when there is one;
    // a flight with neither gets one fixed December 1915 night.
    ...(options.mission?.region ? { region: options.mission.region } : {}),
    ...(options.mission?.era ? { era: options.mission.era } : {}),
    ...(options.careerDate ? { date: options.careerDate } : {}),
  });
  scene.add(dressing.group);

  const aircraft = new THREE.Group();
  scene.add(aircraft);
  // The **swappable** half of the player's aeroplane, and the reason it is a
  // group of its own rather than more children of the pose group above:
  // `scene-boot.ts`'s `loadAircraft` empties it and disposes what was in it
  // whenever ENHANCED MODELS is flipped, so anything else parked beside the mesh
  // would be evicted and disposed with it. That is not hypothetical — 3D
  // COCKPIT's dash quad was added to the pose group and the boot's own first
  // `loadAircraft` took it straight back out again, which is why the dashboard
  // drew nothing at all while reporting itself visible and textured.
  const aircraftModel = new THREE.Group();
  aircraft.add(aircraftModel);
  // 3D COCKPIT's dashboard, hung inside the player's own pose group so it turns
  // with the aeroplane rather than staying nailed to the screen the way the
  // painted panel does (`scene-cockpit-3d.ts`). Hidden until the setting, the
  // view and the airframe all agree (`scene-camera.ts`'s `dashQuadVisible`).
  const playerModelId = bakedModelIdOf(runner.state().config.aircraft) ?? DEFAULT_PLAYER_MODEL;
  const dashQuad = createCockpit3dQuad(eyeOffsetFor(playerAircraftKey({ playerModelId })));
  aircraft.add(dashQuad.object);
  const combat = createCombatVisuals(effects.enhancedModels, effects.enhancedEffects, effects.propellerEffect);
  scene.add(combat.group);
  const sprites = createSpriteEffects();
  scene.add(sprites.group);

  const state: SceneState = {
    scene,
    rig: {
      camera: buildCamera(),
      chasePosition: new THREE.Vector3(),
      flyByAnchorPose: runner.pose(),
      flyByZoom: FLY_BY_ZOOM.default,
      viewportWidthPx: container.clientWidth || 1,
      viewportHeightPx: container.clientHeight || 1,
    },
    // The backend is decided *before* the renderer exists and handed in, because
    // this same call is made a second time by `watchInit` when a WebGPU boot
    // fails (`three/renderer-open.ts`). `state.renderer` is the one mutable
    // field on `SceneState` for exactly that swap.
    rendererPlan,
    renderer: buildRenderer(container, effects.enhancedModels, rendererPlan.mode),
    container,
    runner,
    missionRunner,
    isDogfight,
    // Read off the runner's own aeroplane rather than the mission spec, so a
    // duel's chosen airframe is drawn without the scene needing to know what
    // a duel is.
    playerModelId,
    playerPaint: options.playerPaint,
    aircraft,
    aircraftModel,
    dashQuad,
    dashFeed: options.dashFeed ?? createDashCanvasFeed(),
    // 3D COCKPIT's interior treatment. Made here rather than at load because it
    // has to outlive an ENHANCED MODELS swap: `loadAircraft` hands the same
    // object to every material it builds for the player, and the frame loop
    // writes its uniforms whether or not one has loaded yet.
    cockpitInterior: createCockpitInterior(),
    muzzleFlash: createMuzzleFlashState(),
    aircraftGeneration: 0,
    combat,
    sprites,
    particles: createParticleEffects(scene),
    searchlights: createSearchlights(scene, options.mission?.weather.timeOfDay),
    shadows: createGroundShadows(scene, options.mission?.weather.timeOfDay, groundRef),
    crashMarks: createCrashMarks(scene, groundRef),
    parachute: mountParachute(scene),
    jumpers: createAmbientJumpers(scene, groundRef),
    audio: createFlightAudioEngine(options.audio ?? DEFAULT_AUDIO_SETTINGS),
    propellers: null,
    dressing,
    timeOfDay: options.mission?.weather.timeOfDay,
    skyWord: options.mission?.weather.sky,
    skyOverride: options.skyOverride,
    effects,
    muzzles: new GunMuzzles(),
    cameraMode: DEFAULT_CAMERA_MODE,
    cameraTarget: null,
    flyByZoomGoal: FLY_BY_ZOOM.default,
    setTerrainLineWidth: () => {},
    cockpitView: IDLE_COCKPIT_VIEW,
    cockpitHits: NO_COCKPIT_HITS,
    allowTimeCompression: options.timeCompressionAvailable ?? true,
    sun: sunSuppressionFor(options.sunBlindSpot ?? false, options.mission?.weather.timeOfDay),
    hideWaypoint: options.realNavigation ?? false,
    killCamAvailable: options.killCamAvailable ?? true,
    killCam: createKillCamWindow(options.killCamPlacement ?? null),
    timeCompression: IDLE_TIME_COMPRESSION,
    autopilot: IDLE_AUTOPILOT,
    throttle: runner.state().engine.throttle,
    stick: CENTRED_STICK,
    stickSource: "keyboard",
    stickShaping: options.stickShaping ? stickShapingFor(options.stickShaping) : AUTHENTIC_STICK_SHAPING,
    bailEnabled: options.bailEnabled !== false,
    bailHeldSeconds: 0,
    lastFrameMs: 0,
    lastHudMs: 0,
    paused: false,
    missionEnded: false,
    dogfightEnded: false,
    disposed: false,
    pendingInit: Promise.resolve(),
  };

  const initialGoal = chaseCameraGoal(state.runner.pose());
  state.rig.chasePosition.set(initialGoal.x, initialGoal.y, initialGoal.z);
  state.rig.camera.position.copy(state.rig.chasePosition);
  callbacks.onHud(state.runner.hud());
  callbacks.onCombat(state.runner.combat());
  reportMission(state, callbacks);
  // See `FlightSceneOptions.region`'s doc: `/fly` rides `mission.region`, `/vcr` supplies `region` directly, `/duel`/the dogfight supply neither (Verdun default).
  const flyRegion: MissionRegion | undefined = options.region ?? options.mission?.region;
  // An injected `options.ground` is authoritative and never swapped; an
  // injected `options.groundRef` is the opposite — it exists to be filled
  // (`/vcr` builds its runner before the scene, so it owns the ref).
  state.pendingInit = watchInit(
    state,
    callbacks,
    options.ground && !options.groundRef ? null : groundRef,
    // The scenery ref has no `options.scenery` counterpart to be overridden by:
    // a caller that wants the buildings gets them with the region or not at all.
    sceneryRef,
    flyRegion,
    () => startLoop(state, callbacks),
    // The region's ground has only just become knowable, and a mission whose
    // player starts on his wheels was seated on the flat datum until now.
    (ground) => {
      missionRunner?.reseat(ground);
      if (missionRunner) callbacks.onHud(state.runner.hud());
    },
  );

  // The flight's one resize authority. Watches the container's CSS box **and**
  // its `devicePixelRatio` (`three/view-metrics.ts` has why a bare
  // `ResizeObserver` cannot see the second), de-duplicates the two watches into
  // one notification per real change, and hands the measurement it took to the
  // fan-out so nothing downstream measures the box again.
  //
  // **Nothing on this path reaches the sim.** `resizeViewport` touches the
  // renderer, the camera rig and three screen registrations; it does not call
  // `runner.advance`, does not read or write `SimState`, and does not schedule a
  // tick. That is what lets `/vcr` and `/duel` be resized mid-replay without
  // moving a lockstep hash, and `flight-resize.test.ts` asserts it rather than
  // leaving it to this comment.
  const stopViewMetrics = observeViewMetrics(container, (metrics) => resizeViewport(state, metrics));

  return {
    setCameraMode: (mode) => chooseCameraMode(state, mode),
    setCameraTarget: (target) => chooseCameraTarget(state, target),
    // The sim and the mix stop together. Every pause reason in `/fly` — the P
    // key, the map sheet, the PREFERENCES overlay — funnels through this one
    // call (`Fly.tsx`'s `pauseWanted`), so the audio gate cannot get out of step
    // with the frozen sim it is silencing. Gated here as well as in `onFrame`
    // so the fade starts on the keystroke rather than on the next frame; the
    // third reason a flight goes quiet — it ended — has no keystroke and is
    // `onFrame`'s alone (`silentFlight`).
    setPaused: (paused) => {
      state.paused = paused;
      state.runner.setPaused(paused);
      state.audio.setPaused(silentFlight(state));
    },
    setAudioSettings: (settings) => state.audio.setSettings(settings),
    // AD-007n change 4. Switching shaping leaves the stick where it is — the
    // axes are a physical position, and snapping them to the held keys mid-turn
    // would put in an input the player did not make.
    setStickShaping: (shaping) => {
      state.stickShaping = stickShapingFor(shaping);
    },
    // AD-021. The part-completed hold goes with it: leaving it standing would
    // let a player switch the feature off and back on and find himself a
    // fraction of a second from jumping.
    setBailEnabled: (enabled) => {
      state.bailEnabled = enabled;
      state.bailHeldSeconds = 0;
    },
    // G38's second entry point to AD-021's verb. `bailOut` is idempotent and
    // self-guarding, so this asks nothing first — same as the held key.
    bailOut: () => state.runner.bailOut?.(),
    endSortie: () => endSortie(state, callbacks),
    // Switching KILL CAMERA *on* is the way back from a window the pilot shut
    // by its own ×, which is why this reads the old value before
    // `applyEffectsSettings` overwrites it. Switching it off needs nothing: the
    // window is already gated on the setting every frame.
    setEffectsSettings: (settings) => {
      if (settings.killCamera && !state.effects.killCamera) restoreKillCam(state.killCam);
      applyEffectsSettings(state, settings);
    },
    setKillCamPlacement: (placement) => setKillCamPlacement(state.killCam, placement),
    dismissKillCam: () => dismissKillCam(state.killCam),
    setTimeCompression: (active) => {
      if (!state.allowTimeCompression) return;
      state.timeCompression = { requested: active, active: state.timeCompression.active };
    },
    dispose: () => {
      stopViewMetrics();
      state.audio.dispose();
      state.disposed = true;
      // Stop the loop *before* tearing the scene down: a frame scheduled but
      // not yet run would otherwise render geometry that has just been freed.
      state.renderer.setAnimationLoop(null);
      state.combat.dispose();
      state.sprites.dispose();
      state.particles.dispose();
      state.searchlights.dispose();
      state.shadows.dispose();
      state.crashMarks.dispose();
      state.parachute.dispose();
      state.jumpers.dispose();
      state.propellers?.dispose();
      // Before `disposeTree`, which would otherwise free the quad's geometry and
      // material and leave its `CanvasTexture` — the one thing it owns that a
      // tree walk cannot see, because the canvas behind it belongs to the panel.
      state.dashQuad.dispose();
      disposeTree(scene);
      if (container.contains(state.renderer.domElement)) container.removeChild(state.renderer.domElement);
      // Disposing the renderer before its own init() has settled can throw an
      // unhandled "Instance dropped in popErrorScope" rejection deep inside
      // three.js's WebGPU backend — wait for init to settle either way first.
      void state.pendingInit.finally(() => state.renderer.dispose());
    },
  };
}
